import React from 'react';
import { Icon, IconToken } from '@icons';

export interface IconSelectOption {
	value: string;
	label: string;
	icon?: IconToken | string;
	/** Visual override: provide icon token/name and color class or hex */
	visual?: { icon?: IconToken | string; color?: string };
}

interface IconSelectProps {
	id?: string;
	value: string;
	options: IconSelectOption[];
	onChange: (value: string) => void;
	className?: string;
	openUp?: boolean;
	disabled?: boolean;
}

export const IconSelect: React.FC<IconSelectProps> = ({ id, value, options, onChange, className, openUp = false, disabled = false }) => {
	const [open, setOpen] = React.useState(false);
	const ref = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div className={`relative ${className || ''}`} ref={ref}>
			<button
				id={id}
				type="button"
				className={`w-full flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
				onClick={() => { if (!disabled) setOpen((o) => !o); }}
				aria-haspopup="listbox"
				aria-expanded={open}
				disabled={disabled}
			>
				{(() => {
					const selected = options.find(opt => opt.value === value);
					if (!selected) return null;
					const visIcon = selected.visual?.icon || selected.icon || selected.value;
					const visColor = selected.visual?.color || undefined;
					return (
						<>
							<Icon
								name={visIcon}
								size={22}
								className={`mr-2 ${visColor && visColor.startsWith('text-') ? visColor : ''}`}
								style={visColor && !visColor.startsWith('text-') ? { color: visColor } : undefined}
							/>
							<span className={`capitalize ${visColor && visColor.startsWith('text-') ? visColor : ''}`} style={visColor && !visColor.startsWith('text-') ? { color: visColor } : undefined}>{selected.label}</span>
						</>
					);
				})()}
				<svg className="w-4 h-4 ml-auto text-gray-400" fill="none" viewBox="0 0 20 20" stroke="currentColor">
					<path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>
			{open && !disabled && (
				<ul
					className={`absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto ${open ? '' : ''}`}
					style={openUp ? { bottom: '100%', marginBottom: '0.25rem' } : undefined}
					role="listbox"
				>
						{options.map((opt) => (
						<li
							key={opt.value}
							className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-100 ${opt.value === value ? 'bg-blue-50 font-semibold' : ''}`}
							onClick={() => { onChange(opt.value); setOpen(false); }}
							role="option"
							aria-selected={opt.value === value}
						>
								{(() => {
									const visIcon = opt.visual?.icon || opt.icon || opt.value;
									const visColor = opt.visual?.color || undefined;
										return (
											<>
												<Icon
													name={visIcon}
													size={22}
													className={`mr-2 ${visColor && visColor.startsWith('text-') ? visColor : ''}`}
													style={visColor && !visColor.startsWith('text-') ? { color: visColor } : undefined}
												/>
												<span className={`capitalize ${visColor && visColor.startsWith('text-') ? visColor : ''}`} style={visColor && !visColor.startsWith('text-') ? { color: visColor } : undefined}>{opt.label}</span>
											</>
										);
								})()}
						</li>
							))}
				</ul>
			)}
		</div>
	);
};
